import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useMessaging } from '../../contexts/MessagingContext';
import { useAuth } from '../../contexts/AuthContext';
import MessageThread from './MessageThread';
import { getUserInfo } from '../../services/enhancedFirestore';

const ChatPage = () => {
  const { conversationId } = useParams();
  const navigate = useNavigate();
  const { 
    selectConversation, 
    sendMessage, 
    messages, 
    activeConversation, 
    loading 
  } = useMessaging();
  const { currentUser } = useAuth();
  
  const [newMessage, setNewMessage] = useState('');
  const [partnerInfo, setPartnerInfo] = useState(null);
  const [error, setError] = useState('');
  const [isLoadingPartner, setIsLoadingPartner] = useState(false);
  const threadRef = useRef(null);
  const lastConversationIdRef = useRef(null);
  const partnerInfoFetchedRef = useRef(false);

  // Memoized function to prevent unnecessary re-renders
  const handleBackToMessages = useCallback(() => {
    navigate('/messages');
  }, [navigate]);

  // Select conversation only when conversationId changes and is different
  useEffect(() => {
    if (conversationId && conversationId !== lastConversationIdRef.current) {
      console.log('Selecting conversation:', conversationId);
      lastConversationIdRef.current = conversationId;
      partnerInfoFetchedRef.current = false; // Reset partner info flag
      setPartnerInfo(null); // Reset partner info
      setError(''); // Clear any previous errors
      selectConversation(conversationId);
    }
  }, [conversationId, selectConversation]);

  // Fetch partner info only when active conversation changes and we haven't fetched it yet
  useEffect(() => {
    const fetchPartnerInfo = async () => {
      if (!activeConversation || !currentUser || isLoadingPartner || partnerInfoFetchedRef.current) {
        return;
      }
      
      // Check if this is the conversation we're looking for
      if (activeConversation.id !== conversationId) {
        return;
      }
      
      setIsLoadingPartner(true);
      
      try {
        // Find the partner ID
        const partnerId = activeConversation.participants.find(id => id !== currentUser.uid);
        
        if (!partnerId) {
          console.warn('No partner found in conversation');
          setIsLoadingPartner(false);
          return;
        }
        
        // Check if we already have the info in the conversation
        if (activeConversation.members && activeConversation.members[partnerId]?.displayName) {
          setPartnerInfo({
            id: partnerId,
            displayName: activeConversation.members[partnerId].displayName,
            photoURL: activeConversation.members[partnerId].photoURL
          });
          partnerInfoFetchedRef.current = true;
          setIsLoadingPartner(false);
          return;
        }
        
        // If not, fetch it from Firestore
        console.log('Fetching partner info for:', partnerId);
        const userInfo = await getUserInfo(partnerId);
        if (userInfo) {
          setPartnerInfo({
            id: partnerId,
            displayName: userInfo.displayName || 'Unknown User',
            photoURL: userInfo.photoURL || null
          });
          partnerInfoFetchedRef.current = true;
        }
      } catch (err) {
        console.error('Error fetching partner info:', err);
        setError('Failed to load user information');
      } finally {
        setIsLoadingPartner(false);
      }
    };
    
    fetchPartnerInfo();
  }, [activeConversation?.id, currentUser?.uid, conversationId, isLoadingPartner]);

  // Scroll to bottom when messages change (with throttling)
  useEffect(() => {
    const scrollToBottom = () => {
      if (threadRef.current) {
        const { scrollTop, scrollHeight, clientHeight } = threadRef.current;
        // Only scroll if user is near the bottom (within 100px)
        const isNearBottom = scrollHeight - scrollTop - clientHeight < 100;
        
        if (isNearBottom) {
          threadRef.current.scrollTop = scrollHeight;
        }
      }
    };

    // Use setTimeout to avoid excessive scrolling during rapid message updates
    const timeoutId = setTimeout(scrollToBottom, 100);
    
    return () => clearTimeout(timeoutId);
  }, [messages?.length]); // Only depend on messages length to avoid unnecessary updates

  // Handle send message with error handling
  const handleSendMessage = useCallback(async (e) => {
    e.preventDefault();
    
    if (!newMessage.trim() || !activeConversation) return;
    
    const messageText = newMessage.trim();
    setNewMessage(''); // Clear input immediately for better UX
    
    try {
      await sendMessage(messageText);
    } catch (error) {
      console.error('Error sending message:', error);
      setError(`Failed to send message: ${error.message}`);
      // Restore the message in case of error
      setNewMessage(messageText);
    }
  }, [newMessage, activeConversation, sendMessage]);

  // Get conversation partner name with memoization
  const getPartnerName = useCallback((conversation) => {
    if (!conversation || !currentUser) return 'Unknown';
    
    const partnerId = conversation.participants.find(id => id !== currentUser.uid);
    if (!partnerId) return 'Unknown';
    
    // Try to get name from members object
    if (conversation.members && conversation.members[partnerId]) {
      const displayName = conversation.members[partnerId].displayName;
      if (displayName) return displayName;
    }
    
    // Try from participantMap as fallback
    if (conversation.participantMap && conversation.participantMap[partnerId]) {
      const displayName = conversation.participantMap[partnerId].displayName;
      if (displayName) return displayName;
    }
    
    return 'Unknown';
  }, [currentUser]);

  // Show loading state
  if (loading) {
    return (
      <div className="chat-page">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading conversation...</p>
        </div>
      </div>
    );
  }

  // Show error state if conversation not found
  if (!activeConversation && !loading) {
    return (
      <div className="chat-page">
        <div className="error-container">
          <h2>Conversation not found</h2>
          <p>The conversation you're looking for doesn't exist or you don't have access to it.</p>
          <button onClick={handleBackToMessages} className="btn btn-primary">
            Back to Messages
          </button>
        </div>
      </div>
    );
  }

  // Don't render if we don't have the right conversation loaded
  if (activeConversation && activeConversation.id !== conversationId) {
    return (
      <div className="chat-page">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading conversation...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="chat-page">
      {/* Chat Header */}
      <div className="chat-header">
        <div className="chat-header-left">
          <button 
            onClick={handleBackToMessages} 
            className="back-button"
            aria-label="Back to messages"
          >
            ← Back
          </button>
          <div className="partner-info">
            <div className="partner-avatar">
              {partnerInfo?.photoURL ? (
                <img src={partnerInfo.photoURL} alt={partnerInfo.displayName} />
              ) : (
                <div className="avatar-initials">
                  {partnerInfo?.displayName?.charAt(0)?.toUpperCase() || '?'}
                </div>
              )}
            </div>
            <div className="partner-details">
              <h2>
                {partnerInfo?.displayName || 
                 (activeConversation ? getPartnerName(activeConversation) : 'Loading...')}
              </h2>
              <span className="partner-status">
                {isLoadingPartner ? 'Loading...' : 'Active'}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="error-message">
          {error}
          <button 
            onClick={() => setError('')}
            style={{ marginLeft: '10px', background: 'none', border: 'none', color: 'inherit', textDecoration: 'underline', cursor: 'pointer' }}
          >
            Dismiss
          </button>
        </div>
      )}

      {/* Chat Messages */}
      <div className="chat-content" ref={threadRef}>
        {activeConversation ? (
          <MessageThread 
            messages={messages}
            currentUserId={currentUser?.uid}
            activeConversation={activeConversation}
          />
        ) : (
          <div className="loading-container">
            <p>Loading messages...</p>
          </div>
        )}
      </div>

      {/* Message Input */}
      <div className="chat-input-container">
        <form onSubmit={handleSendMessage} className="chat-input-form">
          <input
            type="text"
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            placeholder={`Type a message to ${
              partnerInfo?.displayName || 
              (activeConversation ? getPartnerName(activeConversation) : 'Unknown')
            }...`}
            className="chat-input"
            disabled={!activeConversation}
          />
          <button 
            type="submit"
            disabled={!newMessage.trim() || !activeConversation}
            className="send-button"
          >
            Send
          </button>
        </form>
      </div>
    </div>
  );
};

export default ChatPage; 